import { Autosuggest, Modal, Text } from '@acin/ui-core';
import React, { useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from '../../../hooks';
import { useReviewObligations } from '../../ReviewObligationsProvider';
import { useCreateInternalRegulationObligationRiskMutation } from '../graphql/mutations/createInternalRegulationObligationRisk.generated';
import { useDeleteInternalRegulationObligationRiskMutation } from '../graphql/mutations/deleteInternalRegulationObligationRisk.generated';
import { useInternalRiskNamesQuery } from '../graphql/queries/internalRiskNames.generated';
import { useObligationRisks } from '../hooks/useObligationRisks';
import { MappingItem } from './MappingItem';
import * as SC from './RiskMappings.styled';

export interface RiskMappingsProps {
  className?: string;
  selectedObligationId?: string;
}

export const RiskMappings: React.FC<RiskMappingsProps> = ({ className, selectedObligationId }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { activeObligation } = useReviewObligations();
  const { risks, loading, pageInfo, handleFetchMore } = useObligationRisks(activeObligation?.id);
  const [localRisks, setLocalRisks] = useState(risks);
  const [searchInput, setSearchInput] = useState('');
  const [riskOptions, setRiskOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRiskForDelete, setSelectedRiskForDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteRisk, { loading: deleteLoading }] = useDeleteInternalRegulationObligationRiskMutation();
  const [createRisk] = useCreateInternalRegulationObligationRiskMutation();
  const { data: riskNamesData } = useInternalRiskNamesQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (riskNamesData?.internalRiskNames) {
      const options = riskNamesData.internalRiskNames.map((name) => ({
        label: name,
        value: name,
      }));
      setRiskOptions(options);
    }
  }, [riskNamesData?.internalRiskNames]);

  React.useEffect(() => {
    setLocalRisks(risks);
  }, [activeObligation?.id, risks?.length]);

  const handleDeleteRisk = (riskId: string) => {
    const risk = localRisks.find((r) => r.id === riskId);
    if (risk) {
      setSelectedRiskForDelete({ id: riskId, name: risk.name ?? '' });
      setIsModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRiskForDelete) return;
    const { errors } = await deleteRisk({
      variables: {
        input: { id: selectedRiskForDelete.id },
      },
      errorPolicy: 'all',
    });
    if (errors?.length) return;
    setLocalRisks((prev) => prev.filter((risk) => risk.id !== selectedRiskForDelete.id));
    setIsModalOpen(false);
    setSelectedRiskForDelete(null);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setSelectedRiskForDelete(null);
  };

  useIntersectionObserver(sentinelRef, handleFetchMore);

  return (
    <SC.RiskMappings className={className}>
      <SC.List>
        {localRisks.map((risk) => (
          <MappingItem key={risk.id} id={risk.id} name={risk.name} onDelete={handleDeleteRisk} />
        ))}
        {loading && pageInfo?.hasNextPage && <SC.LoadingIndicator />}
        {pageInfo?.hasNextPage && <SC.Sentinel ref={sentinelRef} />}
      </SC.List>
      <SC.InputSection>
        <Autosuggest
          placeholder="Add an Index Risk"
          onChange={(value) => setSearchInput(value)}
          onSelect={async (item) => {
            if (!selectedObligationId) return;
            const { data } = await createRisk({
              variables: {
                input: {
                  name: item.label,
                  obligationId: selectedObligationId,
                },
              },
              errorPolicy: 'all',
            });
            if (data?.createInternalRegulationObligationRisk) {
              setLocalRisks((prev) => [
                ...prev,
                { ...data.createInternalRegulationObligationRisk, __typename: 'InternalRegulationObligationRisk' },
              ]);
            }
            setSearchInput('');
          }}
          items={riskOptions}
        />
      </SC.InputSection>
      <Modal
        show={isModalOpen}
        title="Delete this Risk?"
        footerButtonPrimary={{ label: 'Confirm', onClick: handleConfirmDelete, disabled: deleteLoading }}
        footerButtonSecondary={{ label: 'Cancel', onClick: handleCancelDelete }}
      >
        <Text>{`Are you sure you want to delete the following risk: ${selectedRiskForDelete?.name}?`}</Text>
      </Modal>
    </SC.RiskMappings>
  );
};
